import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Grid,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import Loader from '../General/Loader';
import BackButton from '../../components/BackButton';

const DetailedAdEnquiry = () => {
  const [adRequests, setAdRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    fetchAdRequests();
  }, [filterStatus]);

  const fetchAdRequests = async () => {
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/adRequests/detailed${filterStatus ? `?status=${filterStatus}` : ''}`;
      const response = await axios.get(url);
      const processedData = response.data.adRequests.map(request => ({
        id: request._id,
        customerType: request.customerType || 'N/A',
        customerName: request.customerInfo?.customerName || 'N/A',
        customerInfo: `${request.customerInfo?.customerName || 'N/A'} (${request.customerType || 'N/A'})`,
        customerMobile: request.customerInfo?.customerMobile || 'N/A',
        adType: request.adType,
        pincodes: request.pincodes || [],
        usersCount: request.usersCount,
        calculatedAmount: request.calculatedAmount,
        status: request.status,
        createdAt: new Date(request.createdAt).toLocaleDateString(),
      }));
      setAdRequests(processedData);
    } catch (error) {
      showSnackbar('Error fetching ad requests', 'error');
    }
    setLoading(false);
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await axios.patch(`${API_BASE_URL}/adRequests/${requestId}/status`, {
        status: newStatus
      });
      showSnackbar(`Request ${newStatus} successfully`, 'success');
      fetchAdRequests();
    } catch (error) {
      showSnackbar(`Error updating status: ${error.response?.data?.message || 'Unknown error'}`, 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusButtons = (params) => {
    if (!params.row) return null;
    
    const { status, id } = params.row;
    
    switch (status) {
      case 'created':
        return (
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleStatusChange(id, 'verified')}
            >
              Verify
            </Button>
          </Box>
        );
      case 'verified':
        return (
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => handleStatusChange(id, 'accepted')}
            >
              Accept
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => handleStatusChange(id, 'rejected')}
            >
              Reject
            </Button>
          </Box>
        );
      case 'accepted':
        return (
          <Typography color="success.main">Accepted</Typography>
        );
      case 'rejected':
        return (
          <Typography color="error.main">Rejected</Typography>
        );
      default:
        return null;
    }
  };

  const columns = [
    { 
      field: 'customerInfo', 
      headerName: 'Customer Info', 
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      )
    },
    { field: 'customerMobile', headerName: 'Customer Mobile', width: 130 },
    { 
      field: 'adType', 
      headerName: 'Ad Type', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value?.toUpperCase() || 'N/A'} 
          color={params.value === 'banner' ? 'primary' : 'secondary'}
          size="small"
        />
      )
    },
    {
      field: 'pincodes',
      headerName: 'Pincodes',
      width: 200,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ 
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          lineHeight: '1.2',
          padding: '8px 0'
        }}>
          {Array.isArray(params.value) && params.value.map((pincode, index) => (
            <Chip
              key={index}
              label={pincode}
              size="small"
              sx={{ m: 0.5 }}
            />
          ))}
        </Box>
      )
    },
   
    { field: 'status', headerName: 'Status', width: 100 },
    { field: 'createdAt', headerName: 'Created Date', width: 110 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      renderCell: getStatusButtons,
    },
  ];

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
    {/* Place BackButton outside the Grid */}
    <BackButton />
    <br/>  <br/>
    <Box sx={{ height: 'auto', width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Detailed Ad Enquiries
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="created">Created</MenuItem>
              <MenuItem value="verified">Verified</MenuItem>
              <MenuItem value="accepted">Accepted</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {loading ? (
        <Loader />
      ) : (
        <DataGrid
          rows={adRequests}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          autoHeight
          disableSelectionOnClick
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-cell': {
              whiteSpace: 'normal',
              padding: '8px',
            },
          }}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
    </Box>
  );
};

export default DetailedAdEnquiry;